import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { config } from "../../config";
import { moment } from "../../utils/moment";
import { ResponsivePicture } from "../Image";
import { Link } from "../Link";
import { Tags } from "../Tags";
import { Text } from '../Text';
import { formatDatesOverview } from "./EventUtils";
const React = require("react");
const defaultNumberOfVisibleEvents = 3;
export const scrollTargetId = "scrollTarget";
export const nextEvent = (event)=>{
    if (!event.ChildEvents || event.ChildEvents.length == 0) return event;
    const next = event.ChildEvents[0];
    return next.Kind == 'single' ? next : nextNestedEvent(next);
};
export const nextNestedEvent = (event)=>{
    if (!event.Events || event.Events.length == 0) return event;
    const next = event.Events[0];
    return next.Kind == 'single' ? next : nextNestedEvent(next);
};
export const EventCard = (props)=>{
    const { event, linkUrl, onNavigate, variant = 'row' } = props;
    const { Title, Subtitle, TeaserText, Image, Status, Locations, ChildEvents } = event;
    const { arrowIconId } = config().ui;
    const Heading = props.headingLevel ?? 'h4';
    const linkProps = {
        url: linkUrl,
        handle: onNavigate
    };
    const singleLocation = (Locations === null || Locations === void 0 ? void 0 : Locations.length) == 1;
    const hasChildren = (ChildEvents === null || ChildEvents === void 0 ? void 0 : ChildEvents.length) > 1;
    const fauxlinkLabel = !hasChildren ? 'Bekijk activiteit' : singleLocation ? 'Bekijk alle momenten' : 'Bekijk alle datums en locaties';
    // the row variant collapses to an arrow-only button for a single event; stacked always shows the full label
    const arrowOnly = variant == 'row' && !hasChildren;
    const fauxlinkIcon = /*#__PURE__*/ _jsx("svg", {
        className: "a-item__fauxlink-icon",
        width: "16",
        height: "16",
        "aria-hidden": "true",
        children: /*#__PURE__*/ _jsx("use", {
            xlinkHref: arrowIconId
        })
    });
    // a-item--event separates the overview card from other .a-item consumers (search results, AanZet clusters page)
    return /*#__PURE__*/ _jsx("li", {
        className: `a-item a-item--event a-item--${Status} a-item--${variant}`,
        children: /*#__PURE__*/ _jsxs(Link, {
            className: "a-item__link",
            ...linkProps,
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "a-item__content",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "a-item__text",
                            children: [
                                /*#__PURE__*/ _jsxs("header", {
                                    className: "a-item__header",
                                    children: [
                                        /*#__PURE__*/ _jsx(Tags, {
                                            items: event.Tags,
                                            className: "a-item__tags",
                                            limit: 2
                                        }),
                                        /*#__PURE__*/ _jsx(Heading, {
                                            className: "a-item__title title--ms",
                                            children: /*#__PURE__*/ _jsx("span", {
                                                children: Title
                                            })
                                        }),
                                        Subtitle && /*#__PURE__*/ _jsx("span", {
                                            className: "a-item__subtitle label--xs",
                                            children: Subtitle
                                        })
                                    ]
                                }),
                                TeaserText && /*#__PURE__*/ _jsx(Text, {
                                    ...TeaserText,
                                    className: "a-item__teaser text--ms"
                                })
                            ]
                        }),
                        hasChildren ? MultipleEventMeta({
                            event
                        }) : SingleEventMeta({
                            event
                        }),
                        arrowOnly ? /*#__PURE__*/ _jsxs("div", {
                            className: "a-item__fauxlink a-item__fauxlink--arrow",
                            children: [
                                fauxlinkIcon,
                                /*#__PURE__*/ _jsx("span", {
                                    className: "sr-text",
                                    children: fauxlinkLabel
                                })
                            ]
                        }) : /*#__PURE__*/ _jsxs("div", {
                            className: "a-item__fauxlink a-item__fauxlink--all",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    children: fauxlinkLabel
                                }),
                                fauxlinkIcon
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "a-item__visual",
                    children: Image ? /*#__PURE__*/ _jsx(ResponsivePicture, {
                        className: "a-item__image",
                        imgClassName: "a-item__image",
                        defaultImageUrl: Image.Fallback,
                        cropsPerMedia: [
                            {
                                crops: [
                                    {
                                        width: 200,
                                        url: Image.Teaser
                                    },
                                    {
                                        width: 400,
                                        url: Image.Teaser2x
                                    }
                                ]
                            }
                        ],
                        width: 225,
                        height: 225,
                        alt: ""
                    }) : /*#__PURE__*/ _jsx("div", {
                        className: "a-item__placeholder"
                    })
                })
            ]
        })
    });
};
const SingleEventMeta = (props)=>{
    return /*#__PURE__*/ _jsx("div", {
        className: "a-item__meta a-item__meta--single",
        children: GroupedEventMeta({
            ...props
        })
    });
};
const MultipleEventMeta = (props)=>{
    let { event: { ChildEvents } } = props;
    return /*#__PURE__*/ _jsx("ul", {
        className: "a-item__meta--wrapper",
        children: ChildEvents.slice(0, defaultNumberOfVisibleEvents).map((child)=>/*#__PURE__*/ _jsx("li", {
                className: "a-item__meta a-item__meta--row",
                children: /*#__PURE__*/ _jsx(EventMeta, {
                    event: child
                })
            }, `meta__${child.Id}`))
    });
};
const EventMeta = (props)=>{
    var _nextNestedEvent_Locations_;
    const { event: { Start, Status, Locations } } = props;
    // If there's more than 1 location, we attach an alphabetized list as title on the `address` element
    const [locationName, addressAttributes] = Locations.length == 1 ? [
        (_nextNestedEvent_Locations_ = nextNestedEvent(props.event).Locations[0]) === null || _nextNestedEvent_Locations_ === void 0 ? void 0 : _nextNestedEvent_Locations_.Name,
        {}
    ] : [
        "Meerdere locaties",
        {
            title: Locations.map((l)=>l.Name).sort().join('\n')
        }
    ];
    return baseMeta(Start, props, addressAttributes, locationName, Status, true);
};
const GroupedEventMeta = (props)=>{
    var _nextEvent_Locations_;
    const { event: { Start, Status, Locations } } = props;
    // If there's more than 1 location, we attach an alphabetized list as title on the `address` element
    const [locationName, addressAttributes] = Locations.length == 1 ? [
        (_nextEvent_Locations_ = nextEvent(props.event).Locations[0]) === null || _nextEvent_Locations_ === void 0 ? void 0 : _nextEvent_Locations_.Name,
        {}
    ] : [
        "Meerdere locaties",
        {
            title: Locations.map((l)=>l.Name).sort().join('\n')
        }
    ];
    return baseMeta(Start, props, addressAttributes, locationName, Status, false);
};
function baseMeta(Start, props, addressAttributes, locationName, Status, withLocationLabel) {
    const { date, time } = formatDatesOverview(props.event);
    const dateAndTime = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("time", {
                dateTime: moment(Start).format('YYYY-MM-DD'),
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "event__date",
                        children: date
                    }),
                    time && /*#__PURE__*/ _jsx("span", {
                        className: "event__time",
                        children: time
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("address", {
                ...addressAttributes,
                children: [
                    withLocationLabel && /*#__PURE__*/ _jsx("span", {
                        className: "event__location-label",
                        children: "locatie"
                    }),
                    locationName
                ]
            })
        ]
    });
    return Status == 'published' ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "sr-text",
                children: "Waar en wanneer: "
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "event__datetime",
                children: dateAndTime
            })
        ]
    }) : /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "sr-text",
                children: "Deze activiteit is geannuleerd. Het was gepland op: "
            }),
            /*#__PURE__*/ _jsx("del", {
                className: "event__datetime",
                children: dateAndTime
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "event__status",
                "aria-hidden": "true",
                children: "Deze activiteit is geannuleerd."
            })
        ]
    });
}
